import BriefingCard from './briefing-card';
import { motion } from 'motion/react';
import { Users, PieChart, Star, Cloud, ChevronRight, Calendar, ExternalLink, TrendingUp, Clock, AlertTriangle, CheckSquare, GraduationCap, StickyNote, CheckCircle, XCircle, Pencil } from 'lucide-react';
import { useOverviewStats } from '@/src/hooks/useOverviewStats';
import { useWeather } from '@/src/hooks/useWeather';
import { useTraining } from '@/src/hooks/useTraining';
import { useTasks } from '@/src/hooks/useTasks';
import { useTeamAccess } from '@/src/hooks/useTeamAccess';
import { useOrgId } from '@/src/hooks/useOrgId';
import { supabase } from '@/src/lib/supabase';
import type { PendingAction } from '@/src/lib/types';
import { Box, Trash2, ShoppingCart, ClipboardList, Coins, ScanLine, BarChart3, ArrowLeftRight, ChefHat, Calendar as CalendarIcon } from 'lucide-react';
import { FileText, BarChart2, UserCheck, History } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { useAppColors } from '@/src/lib/theme';
import FinishVenueSetup, { type VenueSetupFields } from '@/src/components/dashboard/finish-venue-setup';
import type { NearbyStop } from '@/src/components/dashboard/widgets/nearby-transport-picker';
import { LayoutGrid } from 'lucide-react';
import CustomizeWidgets from '@/src/components/dashboard/widgets/customize-widgets';
import { WIDGET_REGISTRY, DEFAULT_LAYOUT, sizeClass, widgetSizeRank, venueRegion, widgetAllowed } from '@/src/components/dashboard/widgets/registry';
import { useVenueSetupFields } from '@/src/hooks/useVenueSetupFields';
import { useOwnerMetrics } from '@/src/hooks/useOwnerMetrics';
import { formatMoney } from '@/src/lib/format';
import TasksPanel from '@/src/components/dashboard/widgets/tasks-panel';
import TrainingPanel from '@/src/components/dashboard/widgets/training-panel';
import NotesPanel from '@/src/components/dashboard/widgets/notes-panel';
import SnapshotPanel from '@/src/components/dashboard/widgets/snapshot-panel';
import PendingActionsPanel from '@/src/components/dashboard/widgets/pending-actions-panel';

interface OverviewPanelProps {
  venueId: string;
  userId: string;
  userName: string;
  venueName: string;
  userRole: string;
  timezone?: string;
  onSelectSuggestion: (prompt: string) => void;
  pendingActions: PendingAction[];
  onOpenApprovals?: () => void;
  onOpenStock: () => void;
  onOpenRoster: () => void;
  onOpenWastage: () => void;
  onOpenPO: () => void;
  onOpenCount: () => void;
  onOpenScan: () => void;
  onOpenRecipes: () => void;
  onOpenReport: () => void;
  onOpenCogs: () => void;
  onOpenPrice: () => void;
}

const QUICK_ACTIONS = [
  { label: 'What are the urgent tasks?', Icon: AlertTriangle },
  { label: 'Show me open tasks', Icon: CheckSquare },
  { label: 'Who has completed their training?', Icon: GraduationCap },
];

function isVenueSetupComplete(f: VenueSetupFields): boolean {
  const hasLocation = !!(f.address && f.suburb && f.state && f.postcode);
  return !!f.cuisine && hasLocation && !!f.phone && !!f.opening_hours;
}

export default function OverviewPanel({
  venueId, userId, userName, venueName, userRole, timezone, onSelectSuggestion,
  pendingActions, onOpenApprovals, onOpenStock, onOpenRoster, onOpenWastage, onOpenPO,
  onOpenCount, onOpenScan, onOpenRecipes, onOpenReport, onOpenCogs, onOpenPrice,
}: OverviewPanelProps) {
  const c = useAppColors();
  const { stats } = useOverviewStats(venueId, userId);
  const tz = timezone || 'Australia/Sydney';
  const fullDate = new Date().toLocaleDateString('en-AU', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: tz });
  const { fields: setupFields, refresh: refreshSetupFields } = useVenueSetupFields(venueId);
  const canSeeNumbers = userRole === 'owner' || userRole === 'manager';
  const { data: om, loading: omLoading } = useOwnerMetrics(venueId, 'month', canSeeNumbers);
  const revenueCard = omLoading && !om
    ? { label: 'REVENUE', value: '…', sub: 'Loading', Icon: TrendingUp }
    : om?.square?.connected && !om.square.error
      ? { label: 'SALES TODAY', value: formatMoney(om.square.today?.sales ?? 0, { compact: true }), sub: om.square.source === 'ordermate' ? `${om.square.today?.orders ?? 0} covers · OrderMate` : `${om.square.today?.orders ?? 0} orders · Square`, Icon: TrendingUp }
      : om?.xero?.connected
        ? { label: 'REVENUE', value: formatMoney(om.xero.revenue, { compact: true }), sub: `${om.period.label} · Xero`, Icon: TrendingUp }
        : { label: 'REVENUE', value: '—', sub: 'Connect Square or Xero', dot: true, Icon: TrendingUp };
  const labourCard = omLoading && !om
    ? { label: 'LABOUR %', value: '…', sub: 'Loading', Icon: PieChart }
    : !om?.deputy?.connected
      ? { label: 'LABOUR %', value: '—', sub: 'Connect Deputy', dot: true, Icon: PieChart }
      : om.labourPct != null
        ? { label: 'LABOUR %', value: `${om.labourPct}%`, sub: 'Labour cost ÷ revenue', Icon: PieChart }
        : { label: 'LABOUR %', value: '—', sub: !(om.xero?.connected || om.square?.connected) ? 'Connect Square or Xero' : om.deputy?.cost != null ? 'No worked shifts yet' : 'Add pay rates in Deputy', dot: true, Icon: PieChart };
  // Money cards are shortcuts: the full picture lives in Numbers (or connect a system first).
  const go = (page: 'numbers' | 'integrations') => () => window.dispatchEvent(new CustomEvent('opsalo:navigate', { detail: { page } }));
  if (canSeeNumbers) {
    Object.assign(revenueCard, (revenueCard as { dot?: boolean }).dot ? { onClick: go('integrations'), linkLabel: 'Connect' } : { onClick: go('numbers'), linkLabel: 'Numbers' });
    Object.assign(labourCard, (labourCard as { dot?: boolean }).dot && !om?.deputy?.connected ? { onClick: go('integrations'), linkLabel: 'Connect' } : { onClick: go('numbers'), linkLabel: 'Numbers' });
  }
  const weather = useWeather(
    (setupFields?.latitude as number | null) ?? null,
    (setupFields?.longitude as number | null) ?? null,
  );
  const [setupDismissed, setSetupDismissed] = useState(() => localStorage.getItem(`dismissed-setup-${venueId}`) === 'true');
  const showSetupBanner = userRole === 'owner' && setupFields && !isVenueSetupComplete(setupFields) && !setupDismissed;

  const [layout, setLayout] = useState<string[] | null>(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);

  useEffect(() => {
    supabase.from('users').select('dashboard_layout').eq('id', userId).maybeSingle().then(({ data }) => {
      setLayout((data?.dashboard_layout as string[] | null) ?? DEFAULT_LAYOUT);
    });
  }, [userId]);

  async function saveLayout(ids: string[]) {
    setLayout(ids);
    await supabase.from('users').update({ dashboard_layout: ids }).eq('id', userId);
  }

  // Tasks
  const { tasks } = useTasks(venueId, {});
  const { orgId } = useOrgId(venueId);
  const { members, onOpsAlo } = useTeamAccess(orgId, venueId);
  const openTasks = tasks.filter(t => t.status !== 'done');
  const urgentTasks = openTasks.filter(t => t.priority === 'urgent' || t.priority === 'high').slice(0, 4);

  // Training
  const { courses, allAssignments } = useTraining(venueId, userId);
  const assignedIds = new Set(allAssignments.map((a) => a.user_id));
  const trainingPeople = members
    .filter((m) => m.active && (!!m.auth_user_id || assignedIds.has(m.id)))
    .sort((a, b) => Number(assignedIds.has(b.id)) - Number(assignedIds.has(a.id)) || a.name.localeCompare(b.name));
  void onOpsAlo;
  const completedAssignments = allAssignments.filter(a => a.completed_at);
  const passedAssignments = completedAssignments.filter(a => (a.score ?? 0) >= 70);
  const trainingCompletionPct = allAssignments.length > 0
    ? Math.round((completedAssignments.length / allAssignments.length) * 100)
    : 0;

  const snapshotItems = [
    { label: 'Pending actions', value: stats.pendingActions, Icon: TrendingUp },
    { label: 'Open tasks', value: openTasks.length, Icon: CheckSquare },
    { label: 'Training completion', value: `${trainingCompletionPct}%`, Icon: GraduationCap },
  ];

  const widgetContext = {
    weather,
    nearbyStops: (setupFields?.nearby_stops as NearbyStop[] | null) ?? [],
    venueLat: (setupFields?.latitude as number | null) ?? null,
    venueLon: (setupFields?.longitude as number | null) ?? null,
    venueId,
    ownerMetrics: om,
    ownerMetricsLoading: omLoading,
    metrics: {
      revenue: revenueCard,
      labour: labourCard,
      bookings: { label: 'BOOKINGS', value: '—', sub: 'Reservations coming soon', dot: true, Icon: Calendar },
      training: {
        label: 'TRAINING', value: `${trainingCompletionPct}%`,
        sub: `${passedAssignments.length}/${allAssignments.length} passed`,
        Icon: GraduationCap,
      },
    },
    panels: {
      tasks: <TasksPanel openCount={openTasks.length} urgentTasks={urgentTasks} members={members} />,
      // Training: people on OpsAlo, plus anyone already assigned a quiz; those with quizzes first.
      training: <TrainingPanel courseCount={courses.length} members={trainingPeople} allAssignments={allAssignments} />,
      notes: <NotesPanel venueId={venueId} />,
      snapshot: <SnapshotPanel items={snapshotItems} onSelectSuggestion={onSelectSuggestion} />,
      pendingActions: <PendingActionsPanel pendingActions={pendingActions} onOpen={onOpenApprovals} />,
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
      className="px-8 py-8 max-w-5xl mx-auto w-full space-y-5"
    >
      {/* Header */}
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-cream leading-tight">Dashboard</h1>
        <p className="text-sm text-cream-muted mt-1">
          {fullDate}
        </p>
      </div>

      {showSetupBanner && setupFields && (
        <FinishVenueSetup
          venueId={venueId}
          venueName={venueName}
          fields={setupFields}
          onSaved={() => { void refreshSetupFields(); }}
          onDismiss={() => {
            localStorage.setItem(`dismissed-setup-${venueId}`, 'true');
            setSetupDismissed(true);
          }}
        />
      )}

      {canSeeNumbers && <BriefingCard venueId={venueId} userId={userId} isManager tz={tz} />}

      {/* Customizable widget grid — driven by layout (saved order) + registry (available widgets) */}
      <div>
        <div className="flex items-center justify-end mb-2">
          <button
            onClick={() => setCustomizeOpen(true)}
            className="flex items-center gap-1.5 text-xs text-cream-muted hover:text-cream transition-colors"
          >
            <LayoutGrid size={13} /> Customize
          </button>
        </div>
        <div className="grid grid-cols-12 gap-3">
          {(layout ?? DEFAULT_LAYOUT)
            .map((id) => WIDGET_REGISTRY.find((w) => w.id === id))
            .filter((def): def is typeof WIDGET_REGISTRY[number] => !!def && widgetAllowed(def, venueRegion(setupFields?.state, tz)))
            // Hide a system's widget when it isn't connected, instead of an empty "Connect…" card.
            .filter((def) => !(def.id === 'finance' && (!canSeeNumbers || (om && !om.xero?.connected))) && !(def.id === 'staffing' && om && !om.deputy?.connected))
            .sort((a, b) => widgetSizeRank(a.size) - widgetSizeRank(b.size))
            .map((def) => (
              <div key={def.id} className={`${sizeClass(def.size)} h-full`}>
                {def.render(widgetContext)}
              </div>
            ))}
        </div>
      </div>

      {customizeOpen && (
        <CustomizeWidgets
          region={venueRegion(setupFields?.state, tz)}
          activeIds={layout ?? DEFAULT_LAYOUT}
          onChange={saveLayout}
          onClose={() => setCustomizeOpen(false)}
        />
      )}

      {/* Quick action chips */}
      <div className="flex flex-wrap gap-2 pb-4">
        {QUICK_ACTIONS.map((qa) => (
          <button key={qa.label} onClick={() => onSelectSuggestion(qa.label)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-cream-line bg-warm-gray text-sm text-cream-muted hover:text-cream hover:bg-leaf-500/8 hover:border-leaf-400/25 transition-all">
            <qa.Icon size={13} />
            {qa.label}
          </button>
        ))}
      </div>
    </motion.div>
  );
}